import * as actionTypes from '../constants/actionTypes';
import attemptRefresh from "redux-refresh-token";
import { actions } from "../actions/accountActions";
import { SUBSCRIPTION_HEADER_NAME } from '../constants/constants';
import { API_ROOT } from './apiConfig';

// eslint-disable-next-line no-undef
export const CALL_API = "Call API";

export default store => next => action => {
    const callAPI = action[CALL_API];
    if (typeof callAPI === "undefined") {
        return next(action);
    }

    let { endpoint, method, body, headers = {} } = callAPI;
    const { types } = callAPI;

    headers.Pragma = "no-cache";

    if (endpoint === 'logout') {
        // Fake logout call to fake out redux-refresh-token and return the promise
        // that it is expecting.
        return Promise.resolve();
    }

    const token = store.getState().user.accessToken;
    if (typeof token === "string") {
        headers.Authorization = `Bearer ${token}`;
    }

    const subscriptionId = store.getState().user.currentSubscriptionId;
    if (subscriptionId) {
        headers[SUBSCRIPTION_HEADER_NAME] = subscriptionId;
    }

    const actionWith = data => {
        const finalAction = Object.assign({}, action, data);
        delete finalAction[CALL_API];
        return finalAction;
    };

    const [requestType, successType, failureType] = types;
    next(actionWith({ type: requestType }));

    // Only stringify the body if we're not dealing with a multipart 
    // FormData object
    if (body && Object.prototype.toString.call(body) !== "[object FormData]") {
        if (endpoint !== 'account/refresh-token') { // Refresh token would return 415
            headers['Content-Type'] = 'application/json';
        }
        body = JSON.stringify(body);
    }

    /**
     * Declaring the refreshActionCreator here, because redux-refresh-token
     * requires that it be a plain old action creator, and not a thunk-called
     * action.  In here we have access to the store, in order to set the 
     * refreshToken on the body of the request.
     */
    const refreshActionCreator = () => {
        const { refreshToken, isSSO } = store.getState().user;
        const body = {
            refreshToken,
            isSSO
        };
        const headers = {
            'content-type': 'application/json'
        }
        return {
            [CALL_API]: {
                endpoint: 'account/refresh-token',
                method: 'POST',
                body: body,
                headers: headers,
                types: [
                    actionTypes.TOKEN_REFRESH_REQUEST,
                    actionTypes.TOKEN_REFRESH_SUCCESS,
                    actionTypes.TOKEN_REFRESH_FAILURE
                ]
            }
        };
    }

    const setAccessTokenActionCreator = (payload) => {
        return {
            type: actionTypes.TOKEN_REFRESH_SUCCESS,
            payload: payload
        };
    }

    
    return (
        fetch(`${API_ROOT}${endpoint}`, {
            method,
            body,
            headers,
            credentials: 'include'
        })
            // Reads the body stream into Flux Standard Action
            .then(createFSAConverter(successType, failureType))
            .then(
            attemptRefresh({
                action,
                failure: actions.logoutOnAuthorizationFailure,
                next,
                refreshActionCreator: refreshActionCreator,
                setAccessTokenActionCreator: setAccessTokenActionCreator,
                store,
                token
            })
            )
    );
};




/**
 * In order to add a server-side error message to the Flux-Standard Action error
 * response, we have taken the implementation of createFSAConverter from the
 * redux-refresh-token package and defined it here instead of importing the one
 * in the package
 */
const createFSAConverter = (successType, failureType) => {
    return async (response) => {

        if (!response.ok) {
            try {
                const errorBody = await response.json();
                return {
                    error: true,
                    payload: {
                        status: response.status,
                        message: (errorBody && errorBody.message) ? errorBody.message : 'An error has occurred'
                    },
                    type: failureType
                }
            } catch (error) {
                return {
                    error: true,
                    payload: {
                        status: response.status,
                        message: 'An error has occurred'
                    },
                    type: failureType
                }
            }
        }

        const contentType = response.headers.get("Content-Type");
        const emptyCodes = [204, 205];

        const createSuccessType = payload => ({
            payload,
            type: successType
        })

        if (
            emptyCodes.indexOf(response.status) === -1 &&
            contentType &&
            contentType.indexOf("json") !== -1
        ) {
            return response.json().then(createSuccessType);
        } else {
            return createSuccessType();
        }
    }
}
